/**
 * WorkflowStepCard - 类似 Cursor 的工作流步骤卡片
 * 
 * 功能：
 * - 渐进式显示步骤（带动画）
 * - 支持折叠/展开已完成步骤
 * - 工具调用作为子步骤缩进显示
 * - 统一的进度条和状态指示
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import {
    ChevronDown,
    ChevronUp,
    CheckCircle,
    AlertCircle,
    Zap,
    Search,
    Globe,
    FileText,
    Scale,
} from 'lucide-react';
import './WorkflowStepCard.scss';

// 工作流步骤类型
export interface WorkflowStep {
    id: string;
    content: string;
    status: 'pending' | 'running' | 'completed' | 'error';
    type: 'main' | 'sub' | 'tool';
    timestamp: number;
    toolName?: string;
}

// Enhanced workflow event fields from Dynamic Orchestrator
export interface WorkflowPhaseInfo {
    phase?: 'orchestrating' | 'researching' | 'drafting' | 'reviewing' | 'improving';
    phaseLabel?: string;
    activeWorkers?: string[];
    progress?: number;
}

interface WorkflowStepCardProps {
    workflowId: string;
    steps: WorkflowStep[];
    isRunning: boolean;
    onCollapse?: () => void;
    onAccept?: () => void;
    onReject?: () => void;
    reviewStatus?: 'pending' | 'accepted' | 'rejected';
    className?: string;
    currentLanguage?: string;
    /** Current orchestrator phase info */
    phaseInfo?: WorkflowPhaseInfo;
}

// 工具图标映射
const TOOL_ICONS: Record<string, React.ReactNode> = {
    ragSearch: <Search size={12} />,
    omnisearch: <Globe size={12} />,
    templateSearch: <FileText size={12} />,
    legalReference: <Scale size={12} />,
};

// Worker 名称映射（显示友好名称）
const WORKER_LABELS: Record<string, string> = {
    'legal-research': 'Legal Research',
    'template-search': 'Template Search',
    'jurisdiction-config': 'Jurisdiction Config',
    'contract-draft': 'Contract Drafting',
    'general-draft': 'Document Drafting',
    'trust-review': 'Trust Stack Review',
    'improvement': 'Document Improvement',
    'chat': 'Chat',
    'classify': 'Classification',
};

// Phase 标签映射
const PHASE_LABELS: Record<string, string> = {
    'orchestrating': 'Analyzing',
    'researching': 'Researching',
    'drafting': 'Drafting',
    'reviewing': 'Reviewing',
    'improving': 'Improving',
};

export const WorkflowStepCard: React.FC<WorkflowStepCardProps> = ({
    workflowId,
    steps,
    isRunning,
    onCollapse,
    onAccept,
    onReject,
    reviewStatus = 'pending',
    className = '',
    currentLanguage = 'en-US',
    phaseInfo,
}) => {
    const { t } = useTranslation(currentLanguage);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [visibleSteps, setVisibleSteps] = useState<WorkflowStep[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    // 渐进式显示步骤
    useEffect(() => {
        if (steps.length > visibleSteps.length) {
            // 添加新步骤时，延迟显示动画
            const timer = setTimeout(() => {
                setVisibleSteps(steps);
            }, 100);
            return () => clearTimeout(timer);
        } else {
            setVisibleSteps(steps);
        }
    }, [steps]);

    // 自动滚动到最新步骤
    useEffect(() => {
        if (containerRef.current && !isCollapsed) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }, [visibleSteps, isCollapsed]);

    const handleToggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
        onCollapse?.();
    };

    // 计算进度 — use orchestrator progress if available, otherwise compute from steps
    const completedCount = steps.filter(s => s.status === 'completed').length;
    const totalMainSteps = steps.filter(s => s.type === 'main').length;
    const progress = phaseInfo?.progress ?? (totalMainSteps > 0 ? (completedCount / totalMainSteps) * 100 : 0);

    // 获取步骤图标 - Harvey AI 机构简约风格
    const getStepIcon = (step: WorkflowStep) => {
        if (step.type === 'tool' && step.toolName) {
            return TOOL_ICONS[step.toolName] || <Zap size={12} />;
        }

        switch (step.status) {
            case 'running':
                // 使用与 AIThinkingIndicator 相同的自定义 spinner
                return <div className="step-spinner" />;
            case 'completed':
                return <CheckCircle size={14} />;
            case 'error':
                return <AlertCircle size={14} />;
            default:
                return <div className="step-dot" />;
        }
    };

    // 动作按钮处理
    const renderActions = () => {
        if (isRunning) return null;

        if (reviewStatus === 'accepted') {
            return (
                <div className="workflow-status accepted">
                    <CheckCircle size={14} />
                    <span>{t('changesAccepted')}</span>
                </div>
            );
        }

        if (reviewStatus === 'rejected') {
            return (
                <div className="workflow-status rejected">
                    <AlertCircle size={14} />
                    <span>{t('changesRejected')}</span>
                </div>
            );
        }

        if (!onAccept || !onReject) return null;

        return (
            <div className="workflow-actions">
                <button
                    className="action-btn reject"
                    onClick={(e) => {
                        e.stopPropagation();
                        onReject();
                    }}
                >
                    <AlertCircle size={14} />
                    {t('rejectChanges')}
                </button>
                <button
                    className="action-btn accept"
                    onClick={(e) => {
                        e.stopPropagation();
                        onAccept();
                    }}
                >
                    <CheckCircle size={14} />
                    {t('acceptChanges')}
                </button>
            </div>
        );
    };

    return (
        <div className={`workflow-step-card ${className} ${isRunning ? 'running' : ''}`}>
            {/* 头部 */}
            <div className="workflow-card-header" onClick={handleToggleCollapse}>
                <div className="header-left">
                    <div className="workflow-icon">
                        {isRunning ? (
                            <div className="step-spinner" />
                        ) : (
                            <Zap size={16} />
                        )}
                    </div>
                    <span className="workflow-title">{t('workflowTitle')}</span>
                </div>
                <div className="header-right">
                    {isRunning && (
                        <div className="progress-indicator">
                            <div className="progress-bar" style={{ width: `${progress}%` }} />
                        </div>
                    )}
                    <button className="collapse-btn">
                        {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>
            </div>

            {/* Active Workers Panel */}
            {!isCollapsed && phaseInfo?.activeWorkers && phaseInfo.activeWorkers.length > 0 && isRunning && (
                <div className="workflow-active-workers">
                    <div className="active-workers-label">
                        {phaseInfo.phaseLabel || PHASE_LABELS[phaseInfo.phase || ''] || 'Working'}
                    </div>
                    <div className="active-workers-list">
                        {phaseInfo.activeWorkers.map((worker) => (
                            <div key={worker} className="active-worker-chip">
                                <div className="step-spinner" style={{ width: 10, height: 10 }} />
                                <span>{WORKER_LABELS[worker] || worker}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 步骤列表 */}
            {!isCollapsed && (
                <div className="workflow-steps" ref={containerRef}>
                    {visibleSteps.map((step: WorkflowStep, index: number) => (
                        <div
                            key={step.id}
                            className={`workflow-step ${step.type} ${step.status}`}
                            style={{ animationDelay: `${index * 50}ms` }}
                        >
                            <div className={`step-icon ${step.status}`}>
                                {getStepIcon(step)}
                            </div>
                            <div className="step-content">
                                {step.content}
                            </div>
                            {step.status === 'running' && (
                                <div className="step-running-indicator" />
                            )}
                        </div>
                    ))}
                    {renderActions()}
                </div>
            )}

            {/* 折叠状态下显示摘要 */}
            {isCollapsed && (
                <div className="workflow-collapsed-summary">
                    {isRunning ? (
                        <span>{t('workflowRunning')} {completedCount}/{totalMainSteps} {t('workflowStepsCompleted')}</span>
                    ) : (
                        <span>✅ {t('workflowStepsTotal').replace('%{count}', String(steps.length))}</span>
                    )}
                </div>
            )}
        </div>
    );
};

export default WorkflowStepCard;
